import { useEffect, useState } from "react";
import { ArrowLeft, PlusCircle, Pencil, Send, CheckCircle2, History, Loader2, Mail, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { QuoteBuilder, defaults } from "./QuoteBuilder";
import { QuoteView } from "./QuoteView";
import { ContractControls } from "./ContractControls";
import { LifetimeGrant } from "./LifetimeGrant";
import { MessagesPanel } from "./MessagesPanel";
import { Card, Pill, Stat, btnOutline, btnPrimary, errMsg, money, fmtDate } from "./ui";

function QuoteActions({ q, onEdit, onUpdated }) {
  const post = async (path, msg) => {
    try { onUpdated((await api.post(path)).data); toast.success(msg); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap gap-2">
      {["draft", "sent", "changes_requested"].includes(q.status) && <button type="button" className={btnOutline} onClick={() => onEdit(q)} data-testid={`aq-edit-${q.id}`}><Pencil className="w-4 h-4" /> {q.status === "draft" ? "Edit draft" : "Revise & resend"}</button>}
      {q.status === "draft" && <button type="button" className={btnPrimary} onClick={() => post(`/admin/quotes/${q.id}/send`, "Quotation sent")} data-testid={`aq-send-${q.id}`}><Send className="w-4 h-4" /> Send</button>}
      {q.status === "accepted" && <button type="button" className={btnPrimary} onClick={() => post(`/admin/quotes/${q.id}/activate`, "Contract activated")} data-testid={`aq-activate-${q.id}`}><CheckCircle2 className="w-4 h-4" /> {q.complimentary ? "Activate complimentary contract" : "Activate without payment"}</button>}
      {q.change_request && q.status === "changes_requested" && <p className="w-full text-sm text-orange-700">Requested: “{q.change_request}”</p>}
    </div>
  );
}

function Invoices({ org, onUpdated }) {
  const confirm = async (i) => {
    if (!window.confirm(`Confirm payment of ${money(i.amount, i.currency)} for ${i.number}? The contract activates now.`)) return;
    try { onUpdated((await api.post(`/admin/invoices/${i.id}/confirm`)).data); toast.success("Payment confirmed — contract activated"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card data-testid="admin-invoices">
      <p className="font-heading font-semibold text-[#0B2545]">Payments & invoices</p>
      {org.invoices.length === 0 ? <p className="text-sm text-slate-500 mt-2">No invoices yet.</p> : (
        <ul className="mt-3 divide-y divide-slate-100 text-sm">{org.invoices.map((i) => (
          <li key={i.id} className="py-2.5 flex flex-wrap items-center justify-between gap-3">
            <span>{i.number} · {i.quote_ref} · {money(i.amount, i.currency)}{i.transfer_reference ? ` · ref ${i.transfer_reference}` : ""}</span>
            <span className="flex items-center gap-2"><Pill s={i.status} />
              {["awaiting_payment", "pending_confirmation"].includes(i.status) && <button type="button" className={btnOutline} onClick={() => confirm(i)} data-testid={`admin-confirm-${i.number}`}>Confirm payment</button>}</span>
          </li>))}</ul>
      )}
    </Card>
  );
}

function Audit({ org }) {
  return (
    <Card data-testid="admin-audit">
      <p className="font-heading font-semibold text-[#0B2545] flex items-center gap-2"><History className="w-4 h-4" /> Audit log</p>
      <ul className="mt-3 space-y-2.5 max-h-80 overflow-y-auto">{org.audit.map((a) => (
        <li key={a.id} className="text-sm"><span className="text-xs text-slate-400">{new Date(a.created_at).toLocaleString("en-GB")} · {a.actor}</span><p className="text-slate-700"><b>{a.action}</b> — {a.details}</p></li>
      ))}</ul>
    </Card>
  );
}

function RequestTools({ org, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/institutions/${org.id}/resend-emails`);
      const ok = (b) => (b ? "sent" : "failed");
      (data.admin && data.organization ? toast.success : toast.error)(`Your notification: ${ok(data.admin)} · Organization confirmation: ${ok(data.organization)}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete the request from ${org.name}? Quotations and messages for it are removed. The account can then submit a new request.`)) return;
    try { await api.delete(`/admin/institutions/${org.id}`); toast.success("Request deleted"); onDeleted(); } catch (e) { toast.error(errMsg(e)); }
  };
  const deletable = !org.contract && org.contract_history.length === 0 && org.members.length === 0;
  return (
    <div className="flex flex-wrap gap-2" data-testid="admin-request-tools">
      <button type="button" className={btnOutline} disabled={busy} onClick={resend} data-testid="admin-resend-emails">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Resend request emails
      </button>
      {deletable && <button type="button" className={`${btnOutline} text-red-600 border-red-200 hover:bg-red-50`} onClick={remove} data-testid="admin-delete-request"><Trash2 className="w-4 h-4" /> Delete request</button>}
    </div>
  );
}

export function AdminOrgDetail({ id, onBack }) {
  const [org, setOrg] = useState(null);
  const [builder, setBuilder] = useState(null);
  useEffect(() => { api.get(`/admin/institutions/${id}`).then(({ data }) => setOrg(data)).catch((e) => toast.error(errMsg(e))); }, [id]);
  if (!org) return <Loader2 className="w-6 h-6 animate-spin text-slate-400" />;
  const saved = (d) => { setOrg(d); setBuilder(null); };
  const renewal = ["active", "grace", "suspended", "scheduled"].includes(org.contract?.status);
  return (
    <div className="space-y-5" data-testid="admin-org-detail">
      <button type="button" onClick={onBack} className="text-sm text-[#1C6FE3] flex items-center gap-1" data-testid="admin-org-back"><ArrowLeft className="w-4 h-4" /> All organizations</button>
      <div className="flex flex-wrap items-center gap-3"><h2 className="font-heading text-2xl font-bold text-[#0B2545]">{org.name}</h2><Pill s={org.status} testid="admin-org-status" /></div>
      <p className="text-sm text-slate-600">{org.country} · {org.contact_name} · {org.admin_email} · {org.phone} · requested {org.dentists} dentists / {org.duration_months} months{org.requirements ? ` · “${org.requirements}”` : ""}</p>
      <RequestTools org={org} onDeleted={onBack} />
      <div className="grid sm:grid-cols-3 gap-4">
        <Stat label="Licenses" value={`${org.seats.active + org.seats.invited} / ${org.seats.licenses}`} sub={`${org.seats.active} active · ${org.seats.invited} invited`} />
        <Stat label="Contract" value={org.contract ? org.contract.status : "—"} sub={org.contract ? `${fmtDate(org.contract.start)} → ${fmtDate(org.contract.end)}` : "None"} />
        <Stat label="Renewals" value={org.contract_history.length} sub="previous contract periods" />
      </div>
      {builder ? <QuoteBuilder org={org} initial={builder.initial} editId={builder.editId} onSaved={saved} onCancel={() => setBuilder(null)} /> : (
        <button type="button" className={btnPrimary} onClick={() => setBuilder({ initial: defaults(org, renewal ? { kind: "renewal" } : {}) })} data-testid="admin-new-quote">
          <PlusCircle className="w-4 h-4" /> {renewal ? "Prepare renewal quotation" : "New quotation"}
        </button>
      )}
      {org.quotes.filter((q) => q.status !== "superseded").map((q) => (
        <QuoteView key={q.id} q={q}><QuoteActions q={q} onUpdated={setOrg} onEdit={(x) => setBuilder({ editId: x.id, initial: { ...defaults(org), ...x } })} /></QuoteView>
      ))}
      <Invoices org={org} onUpdated={setOrg} />
      <ContractControls key={`${org.contract?.ref}-${org.contract?.end}-${org.contract?.licenses}`} org={org} onUpdated={setOrg} />
      <LifetimeGrant key={`lg-${org.contract?.ref}`} org={org} onUpdated={setOrg} />
      <Card className="p-0 overflow-x-auto"><p className="font-heading font-semibold text-[#0B2545] px-5 pt-5">Dentists (names & license status only)</p>
        <table className="w-full text-sm mt-3"><tbody>{org.members.map((m) => (
          <tr key={m.id} className="border-t border-slate-100"><td className="px-5 py-2.5">{m.name || "—"}</td><td className="px-5 py-2.5 text-slate-500">{m.email}</td><td className="px-5 py-2.5"><Pill s={m.status} /></td></tr>
        ))}</tbody></table>
        {org.members.length === 0 && <p className="text-sm text-slate-500 px-5 pb-5">No dentists assigned.</p>}
      </Card>
      <MessagesPanel base={`/admin/institutions/${org.id}/messages`} side="admin" title={`Messages with ${org.name}`} />
      <Audit org={org} />
    </div>
  );
}
